/**
 *  @class
 *  @function ProductComparePanel
 */
if (!customElements.get('product-compare-panel')) {
	class ProductComparePanel extends HTMLElement {
		constructor() {
			super();
			this.toggle = this.querySelector('.compare-toggle');
			this.drawer = this.querySelector('.compare-drawer');
			this.closeButton = this.querySelector('.compare-panel-close');
			this.clear = this.querySelector('.compare-clear');
			this.start = this.querySelector('.compare-start');
			this.modal = this.querySelector('.product-compare-modal');
			this.table = this.querySelector('product-compare-table');
			this.max = Number(this.dataset.max) || 3;
			this.mobileMax = Number(this.dataset.mobileMax) || this.max;
			this.mobileMedia = window.matchMedia('(max-width: 767px)');
			this.isTopDropdown = this.classList.contains('compare-parent--top');
			this.renderRequest = 0;
			this.href = '';
		}

		connectedCallback() {
			if (this.toggle) {
				setTimeout(() => this.toggle.classList.add('compare-toggle--active'), 250);
			}
			this.addEventListeners();
			this.trimStoredProducts();
			this.updateCompareCheckboxes();
			this.updateCompareCounters();
			this.updateStartButton();
			if (this.isTopDropdown && this.getCompareCount()) {
				this.openDrawer();
				this.renderProducts();
			}
			if (this.isTopDropdown) {
				this.modalObserver = new MutationObserver(() => {
					const isModalOpen = this.modal.hasAttribute('open');
					if (isModalOpen) {
						this.modalWasOpen = true;
						return;
					}

					if (this.modalWasOpen) {
						this.modalWasOpen = false;
						this.clearComparison();
						return;
					}

					this.modalWasOpen = false;
				});
				this.modalObserver.observe(this.modal, { attributes: true, attributeFilter: ['open'] });
			}
			this.mobileMedia.addEventListener?.('change', this.onBreakpointChange.bind(this));
		}

		addEventListeners() {
			this.toggle?.addEventListener('click', this.onButtonClick.bind(this));
			this.closeButton?.addEventListener('click', this.onPanelClose.bind(this));
			this.drawer.addEventListener('click', this.onRemoveClick.bind(this));
			this.clear.addEventListener('click', this.onClearClick.bind(this));
			this.start.addEventListener('click', this.onStartClick.bind(this));
			document.addEventListener('change', this.onCheckboxClick.bind(this));
		}

		getSelectedProducts() {
			try {
				const products = JSON.parse(localStorage.getItem('compare-products-vision'));
				return Array.isArray(products) ? products : [];
			} catch (error) {
				return [];
			}
		}

		trimStoredProducts() {
			const products = this.getSelectedProducts();
			const max = this.getCurrentMax();
			if (products.length > max) {
				localStorage.setItem('compare-products-vision', JSON.stringify(products.slice(0, max)));
			}
		}

		getCurrentMax() {
			return this.mobileMedia.matches ? this.mobileMax : this.max;
		}

		addToCompare(productId, productUrl, productHandle) {
			const compareProducts = this.getSelectedProducts();
			if (compareProducts.length < this.getCurrentMax() && !compareProducts.some(product => product.id === productId)) {
				compareProducts.push({ id: productId, url: productUrl, handle: productHandle });
				localStorage.setItem('compare-products-vision', JSON.stringify(compareProducts));
				this.updateCompareComponents();
				return true;
			}
			return false;
		}

		removeFromCompare(productId) {
			const compareProducts = this.getSelectedProducts().filter(product => product.id !== productId);
			localStorage.setItem('compare-products-vision', JSON.stringify(compareProducts));
			this.updateCompareComponents();
		}

		updateStartButton() {
			this.start.disabled = this.getCompareCount() < 2;
		}

		async onCheckboxClick(event) {
			if (!event.target.classList.contains('product-card-compare--checkbox')) return;

			const checkbox = event.target;
			const { productId, productUrl, productHandle } = checkbox.dataset;
			if (checkbox.checked) {
				const added = this.addToCompare(productId, productUrl, productHandle);
				if (!added && !this.getSelectedProducts().some(product => product.id === productId)) {
					checkbox.checked = false;
				}
			} else {
				this.removeFromCompare(productId);
			}

			if (this.isTopDropdown) {
				if (this.getCompareCount()) {
					this.openDrawer();
				} else {
					this.closeDrawer();
				}
				await this.renderProducts();
			}
		}

		updateCompareCheckboxes() {
			const compareProducts = this.getSelectedProducts();
			const compareCount = this.getCompareCount();
			const max = this.getCurrentMax();
			document.querySelectorAll('.product-card-compare--checkbox').forEach(checkbox => {
				const isChecked = compareProducts.some(product => product.id === checkbox.dataset.productId);
				const isDisabled = !isChecked && compareCount >= max;
				const compareControl = checkbox.closest('.product-card-compare');
				const label = checkbox.nextElementSibling;
				checkbox.checked = isChecked;
				checkbox.disabled = isDisabled;
				compareControl?.classList.toggle('is-disabled', isDisabled);
				if (label?.tagName === 'LABEL') {
					if (isDisabled) {
						label.setAttribute('aria-disabled', 'true');
					} else {
						label.removeAttribute('aria-disabled');
					}
				}
			});
		}

		async onRemoveClick(event) {
			const removeButton = event.target.closest('.compare-remove');
			if (!removeButton) return;

			event.preventDefault();
			this.removeFromCompare(removeButton.dataset.productId);
			await this.renderProducts();
			if (this.isTopDropdown && !this.getCompareCount()) this.closeDrawer();
		}

		onClearClick(event) {
			event.preventDefault();
			this.clearComparison();
		}

		clearComparison() {
			this.renderRequest += 1;
			localStorage.setItem('compare-products-vision', JSON.stringify([]));
			this.updateCompareComponents();
			this.drawer.querySelector('.compare-drawer--list').innerHTML = this.getPlaceholderHtml(this.getCurrentMax());
			this.closeDrawer();
		}

		onPanelClose(event) {
			if (this.isTopDropdown && this.getCompareCount()) {
				this.onClearClick(event);
				return;
			}
			this.closeDrawer();
		}

		onStartClick() {
			if (this.getCompareCount() < 2) return;
			if (this.isTopDropdown && this.mobileMedia.matches) {
				this.closeDrawer();
			}
			this.modal.show();
			this.table.dispatchEvent(new CustomEvent('render-product-compare-modal', { bubbles: true }));
		}

		openDrawer() {
			this.drawer.removeAttribute('inert');
			this.drawer.focus();
			this.drawer.classList.add('active');
			if (!this.isTopDropdown) document.body.classList.add('open-cc');
		}

		closeDrawer() {
			this.drawer.setAttribute('inert', '');
			this.drawer.blur();
			this.drawer.classList.remove('active');
			if (!this.isTopDropdown) document.body.classList.remove('open-cc');
		}

		async onButtonClick() {
			await this.renderProducts();
			this.openDrawer();
		}

		async onBreakpointChange() {
			this.trimStoredProducts();
			this.updateCompareComponents();
			if (this.isTopDropdown && this.getCompareCount()) {
				await this.renderProducts();
				if (!this.modal.hasAttribute('open')) this.openDrawer();
			} else if (this.isTopDropdown) {
				this.closeDrawer();
			}
		}

		getCompareCount() {
			return this.getSelectedProducts().length;
		}

		updateCompareCounters() {
			const count = this.getCompareCount();
			this.querySelectorAll('.compare-counter').forEach(counter => {
				counter.textContent = count;
			});
		}

		async renderProducts() {
			const requestId = ++this.renderRequest;
			const compareProducts = this.getSelectedProducts();
			const returnArray = await Promise.all(compareProducts.map(async (product) => {
				try {
					const separator = product.url.includes('?') ? '&' : '?';
					const response = await fetch(`${product.url}${separator}sections=product-compare-card`);
					const data = await response.json();
					return this.getSectionInnerHTML(data['product-compare-card'], '.shopify-section');
				} catch (error) {
					return '';
				}
			}));

			if (requestId !== this.renderRequest) return;
			const renderedProducts = returnArray.filter(Boolean);
			const returnHtml = renderedProducts.join('') + this.getPlaceholderHtml(this.getCurrentMax() - renderedProducts.length);
			this.drawer.querySelector('.compare-drawer--list').innerHTML = returnHtml;
		}

		getPlaceholderHtml(count) {
			const label = this.isTopDropdown ? '<span>Add Another Device to Compare</span>' : '';
			return `<div class="compare-drawer--list-item">${label}</div>`.repeat(Math.max(0, count));
		}

		getSectionInnerHTML(html, selector) {
			return new DOMParser()
				.parseFromString(html, 'text/html')
				.querySelector(selector)?.innerHTML || '';
		}

		updateCompareComponents() {
			this.updateCompareCheckboxes();
			this.updateCompareCounters();
			this.updateStartButton();
		}
	}

	customElements.define('product-compare-panel', ProductComparePanel);
}
